import React, { useContext, useEffect, useState } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import ProjectCard from '../../components/ProjectCard/ProjectCard';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { FLATS_PAGE, PARKING_PAGE, BUSINESS_PAGE } from '../../router/paths';
import styles from './ProjectsPage.module.css'
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import api from '../../utils/api';
import { transformProjectFeatures, transformBusinesses, transformFlats, transformParkings } from '../../utils/transformers';

interface Project {
  projectId: string;
  title?: string;
  description: string;
  preview: string;
  features: {
    id: number;
    text: string;
    image?: string;
  }[];
  buttons: {
    text: string;
  }[];
  image?: string;
  minPrice?: number;
}

function getDeclension(number: number, one: string, few: string, many: string): string {
  const mod10 = number % 10;
  const mod100 = number % 100;

  if (mod100 >= 11 && mod100 <= 14) {
    return many;
  }
  if (mod10 === 1) {
    return one;
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }
  return many;
}

export default function ProjectsPage() {
  const [projectsData, setProjectsData] = useState<Project[]>([]);
  const navigate = useNavigate();
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);

  useEffect(() => {
    document.documentElement.classList.add('black');
    document.body.classList.add('black');

    return () => {
      document.documentElement.classList.remove('black');
      document.body.classList.remove('black');
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoad(true);
      try {
        const [projectsResponse, businessResponse, flatsResponse, parkingResponse] = await Promise.all([
          api.projects.getAll(),
          api.business.getAll(),
          api.flats.getAll(),
          api.parking.getAll()
        ]);

        const projectFeatures = transformProjectFeatures(projectsResponse);
        const businesses = transformBusinesses(businessResponse);
        const flats = transformFlats(flatsResponse);
        const parkings = transformParkings(parkingResponse);

        const projects: Project[] = projectFeatures.map((project) => {
          const availableFlats = flats.filter(flat => flat.available === true);
          const availableParkings = parkings.filter(parking => parking.available === true);
          const availableBusinesses = businesses.filter(business => 
            business.available === true && business.type === 'business'
          );
          
          const flatsCount = availableFlats.length;
          const parkingsCount = availableParkings.length;
          const businessesCount = availableBusinesses.length;
          
          let buttons = [
            { text: `${flatsCount} ${getDeclension(flatsCount, 'квартира', 'квартиры', 'квартир')}` },
            { text: `${parkingsCount} ${getDeclension(parkingsCount, 'машиноместо', 'машиноместа', 'машиномест')}` },
            { text: `${businessesCount} ${getDeclension(businessesCount, 'помещение', 'помещения', 'помещений')}` }
          ];
          
          let minPrice: number | undefined = undefined;
          if (availableFlats.length > 0) {
            const prices = availableFlats.map((flat) => {
              const price = typeof flat.price === 'string' ? parseFloat(flat.price.replace(/ /g, '')) : parseFloat(flat.price);
              const area = typeof flat.area === 'string' ? parseFloat(flat.area.replace(/ /g, '')) : parseFloat(flat.area);
              const discountPerSqm = flat.discountProperty ?? 0;
              const totalDiscount = discountPerSqm * area;
              let finalPrice = price - totalDiscount;
              if (finalPrice < 0) finalPrice = 0;
              return finalPrice;
            });
            const filteredPrices = prices.filter((price: number) => price > 0 && !isNaN(price));
            minPrice = filteredPrices.length > 0 ? Math.min(...filteredPrices) : undefined;
          }
          
          let features = Array.isArray(project.features) ? [...project.features] : [];
          if (minPrice !== undefined) {
            const formattedPrice = minPrice.toLocaleString('ru-RU');
            features.push({
              id: features.length + 1,
              text: `От ${formattedPrice} ₽`,
              image: undefined,
            });
          }
          
          return {
            projectId: project.projectId,
            title: project.title,
            description: project.description,
            preview: project.preview,
            features,
            image: project.image,
            minPrice,
            buttons
          };
        });
        
        setProjectsData(projects);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };
    fetchData();
  }, [setIsInitialLoad]);

  const handleButtonClick = (buttonText: string, projectId: string) => {
    if (buttonText.includes('квартира') || buttonText.includes('квартиры') || buttonText.includes('квартир')) {
      navigate(FLATS_PAGE);
    } else if (buttonText.includes('машиноместо') || buttonText.includes('машиноместа') || buttonText.includes('машиномест')) {
      navigate(PARKING_PAGE);
    } else if (buttonText.includes('помещение') || buttonText.includes('помещения') || buttonText.includes('помещений')) {
      navigate(BUSINESS_PAGE);
    } else {
      navigate(`/projects/status`);
    }
  };  

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Проекты | Восток Строй</title>
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/projects" />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/projects" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>
      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          <Header theme={HeaderType.Dark} />
          <div className={styles.wrapper}>
            {projectsData.map((project, index) => (
              <ProjectCard
                key={project.projectId.toString()}
                projectId={project.projectId.toString()}
                title={project.title ?? ''}
                buttons={project.buttons}
                description={project.description}
                features={project.features}
                image={project.image}
                preview={project.preview}
                onButtonClick={(buttonText) => handleButtonClick(buttonText, project.projectId)}
              />
            ))}
            <div className={styles.info_wrapper}>
              <div className={styles.info_card}>
                <h4>Сдано 9 проектов</h4>
                <p className='text_subheaders_sh2'>Остров, Атмосфера, Ситилайф, Мраморный, Элита Каспия, Профсоюзная, 46, Зейнудина Батманова, 14, Омарова, 9б. Чтобы узнать об&nbsp;этих проектах больше, достаточно посетить наш офис.</p>
              </div>
            </div>
          </div>
          <Footer theme={FooterType.Dark}></Footer>
        </>
      )}
    </div>
  );
}